import { createSlice } from "@reduxjs/toolkit";
import { encode } from "js-base64";
import { SM_REGION, SM_COUNTRY } from "../i18n";

import configMap from "../data/config";
import materialsMap from "../data/materials";

function shouldUpdateFrameRepositionableParts(part, attr) {
  return part === "FRAME" && (attr === "angle" || attr === "inset");
}

function shouldUpdateBackrestRepositionableParts(part, attr) {
  return (
    part === "BACKREST" && (attr === "backrest" || attr === "push_handles")
  );
}

export const configSlice = createSlice({
  name: "config",
  initialState: {
    _code: "",
    _region: SM_REGION,
    _country: SM_COUNTRY,
    _defaultsLoaded: false,
    _updatedPart: null,
    _updatedAttr: null,
    _framePath: null,
    _pushbarPath: null,
    FRAME: {},
    SEAT_SLING: {},
    BACKREST: {},
    SIDEGUARDS: {},
    FOOTREST: {},
    CASTERS: {},
    REAR_WHEELS: {},
    WHEEL_LOCKS: {},
    ADDONS: {},
  },
  reducers: {
    set: (state, action) => {
      const { part, attr, option } = action.payload;
      const { value, constraints } = option;

      state[part][attr] = value;
      state._updatedPart = part;
      state._updatedAttr = attr;

      if (constraints) {
        constraints.forEach((c) => {
          const constrainedValue = state[c.part][c.attr];
          if (!c.values.find((val) => val === constrainedValue)) {
            state[c.part][c.attr] = c.values[0] || null;
          }
        });
      }

      // @todo reload default if a constraint has set the part to NULL
      // This is the case of the backrest push handle / push bar
      // ATM we restore default by adding a "positive" constraint to the other
      // parts, but that should be implemented as a "resetDefaults" propery.
      // For that we need to read the configMap here and find default

      /**
       * shouldUpdateFrameRepositionableParts:
       *
       * When FRAME angle|inset is changed we update the frame_path - this
       * change will make Viewer ignore the angle|inset change and just process
       * the frame_path change which triggers an udpate of the frame
       * repositionable parts: lower_frame, forks, casters and footrest
       *
       */
      if (shouldUpdateFrameRepositionableParts(part, attr)) {
        const angle = state[part].angle;
        const inset = state[part].inset;

        // undefined should only happen on default (initial) parts set
        // because we set the 'angle' before the inset
        if (typeof inset !== "undefined") {
          state._framePath = `${angle}_Degree_${inset}_Cm_Inset`;
        }
      }

      if (shouldUpdateBackrestRepositionableParts(part, attr)) {
        const push_handles = state[part].push_handles;
        state._pushbarPath = `Push_Bar_Options/FOR_${push_handles}`;
      }
    },
    setDefaultsLoaded: (state, action) => {
      state._defaultsLoaded = action.payload;
    },
    buildConfigCode: (state) => {
      const materials = materialsMap.MATERIALS;
      let code = "v1-"; // share link version

      for (const partKey in configMap) {
        const part = configMap[partKey];

        for (const attrKey in part) {
          const attr = part[attrKey];
          if (attr.hidden) {
            continue;
          }

          const selectedValue = state[partKey][attrKey];

          if (!selectedValue) {
            continue;
          }

          if (attr.assetType === "material") {
            const selectedMaterial = materials[selectedValue];
            if (selectedMaterial) {
              code += selectedMaterial.linkKey;
            } else {
              code += "m!";
            }
          } else {
            const isRegionalOption = attr.regional || false;
            const options = isRegionalOption
              ? attr.options[state._region]
              : attr.options;
            const selectedOption = options.find(
              (el) => el.value === selectedValue
            );

            if (selectedOption) {
              code += selectedOption.linkKey || "o!";
            } else {
              code += "o?";
            }
          }
        }

        code += "-";
      }

      code = code.replace(/-$/, ""); // remove trailing dash

      state._code = encode(code);
    },
  },
});

export const { set, setDefaultsLoaded, buildConfigCode } = configSlice.actions;

export default configSlice.reducer;
